import React from 'react'
import { Alert, Autocomplete, TextField, Typography } from '@mui/material'
import { AdvancedTrackLead } from '../../api/types'
import { advancedColors } from '../../theme'
import { LeadOption } from './useLeadDirectory'

type Props = {
  options: LeadOption[]
  value: AdvancedTrackLead
  onChange: (lead: AdvancedTrackLead) => void
  label?: string
  // Set when the track's owner role has nobody on the care team, so the gap is said out loud.
  missingRole?: string | null
}

const LeadPicker = ({ options, value, onChange, label = 'Led by', missingRole = null }: Props) => {
  const selected = value ? options.find((option) => option.providerUid === value.providerUid) ?? null : null

  return (
    <>
      {missingRole && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Nobody on this patient&apos;s care team is a {missingRole} yet. Leave this empty and the patient chooses one
          when they book their intake call, or pick someone now to add them to the care team.
        </Alert>
      )}
      <Autocomplete
        size="small"
        options={options}
        groupBy={(option) => option.group}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, current) => option.providerUid === current.providerUid}
        value={selected}
        onChange={(_event, option) =>
          onChange(option ? { providerUid: option.providerUid, joinAs: option.joinAs } : null)
        }
        renderInput={(params) => <TextField {...params} label={label} placeholder="No lead" />}
      />
      {selected?.joinAs && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: advancedColors.inkMuted }}>
          This adds them to the patient&apos;s care team, which gives them access to this patient.
        </Typography>
      )}
    </>
  )
}

export default LeadPicker
